'use client';

import Link from 'next/link';
import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

type Etapa = 'email' | 'senha' | 'cancelado' | 'nao_encontrado' | 'bloqueado';

export default function EntrarPage() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [etapa, setEtapa] = useState<Etapa>('email');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [motivoCancelado, setMotivoCancelado] = useState<string>('');
  const [bloqueioFim, setBloqueioFim] = useState<number>(0);
  const [contagem, setContagem] = useState<number>(0);
  const senhaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem('startgram_email');
    if (saved) setEmail(saved);
  }, []);

  useEffect(() => {
    if (etapa === 'senha' && senhaRef.current) senhaRef.current.focus();
  }, [etapa]);

  // Countdown do bloqueio (rate limit)
  useEffect(() => {
    if (etapa !== 'bloqueado' || bloqueioFim <= Date.now()) return;
    const tick = () => {
      const restantes = Math.max(0, Math.ceil((bloqueioFim - Date.now()) / 1000));
      setContagem(restantes);
      if (restantes <= 0) {
        setEtapa('email');
        setErro('');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [etapa, bloqueioFim]);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErro('');
    try {
      const res = await fetch('/api/auth/entrar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, senha: senha || undefined }),
      });
      const data = await res.json();

      if (res.ok && data.ok) {
        localStorage.setItem('startgram_email', email);
        window.location.href = data.is_admin ? '/admin/suporte' : '/';
        return;
      }

      if (res.status === 401 && data.requer_senha) {
        setEtapa('senha');
        setLoading(false);
        return;
      }

      if (res.status === 401 && data.error === 'senha_incorreta') {
        setErro('Senha incorreta. Tenta de novo ou usa "Esqueci minha senha".');
        setLoading(false);
        return;
      }

      if (res.status === 404 && data.error === 'email_nao_encontrado') {
        setEtapa('nao_encontrado');
        setLoading(false);
        return;
      }

      if (res.status === 410 && data.error === 'acesso_cancelado') {
        setMotivoCancelado(data.motivo || 'cancelada');
        setEtapa('cancelado');
        setLoading(false);
        return;
      }

      if (res.status === 429 && data.error === 'muitas_tentativas') {
        const fim = new Date(data.bloqueado_ate).getTime();
        setBloqueioFim(fim);
        setEtapa('bloqueado');
        setLoading(false);
        return;
      }

      setErro(data.error || 'Algo deu errado. Tenta de novo.');
      setLoading(false);
    } catch (err: any) {
      setErro(err?.message || 'Erro de conexão');
      setLoading(false);
    }
  }

  function trocarEmail() {
    setEtapa('email');
    setSenha('');
    setErro('');
    setMotivoCancelado('');
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-marca-soft via-marca-soft to-atencao-soft">
      <div className="max-w-sm w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="flex justify-center mb-4">
          <div className="relative w-20 h-20">
            <Image src="/mel/v3/idle_256.jpg" alt="Mel" fill className="object-cover object-top" />
          </div>
        </div>
        <h1 className="text-2xl font-extrabold text-center mb-1 bg-gradient-to-r from-marca to-marca-2 bg-clip-text text-transparent">
          StartGram
        </h1>

        {/* Etapa: email */}
        {etapa === 'email' && (
          <>
            <p className="text-center text-tinta-3 text-sm mb-6">
              Entra com o email da tua compra
            </p>
            <form onSubmit={enviar} className="space-y-3">
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu email"
                className="w-full px-4 py-3 rounded-lg border border-linha focus:border-marca/40 focus:ring-2 focus:ring-marca-soft outline-none"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-marca to-marca-2-ink hover:opacity-95 disabled:opacity-50 transition"
              >
                {loading ? 'Verificando...' : 'Entrar'}
              </button>
              {erro && <p className="text-red-600 text-sm text-center">{erro}</p>}
            </form>
          </>
        )}

        {/* Etapa: senha */}
        {etapa === 'senha' && (
          <>
            <p className="text-center text-tinta-3 text-sm mb-1">
              <span className="font-medium text-tinta-2">{email}</span>
            </p>
            <p className="text-center text-tinta-3 text-sm mb-6">
              Essa conta tem senha. Digita ela:
            </p>
            <form onSubmit={enviar} className="space-y-3">
              <input
                ref={senhaRef}
                type="password"
                required
                value={senha}
                onChange={e => setSenha(e.target.value)}
                placeholder="sua senha"
                className="w-full px-4 py-3 rounded-lg border border-linha focus:border-marca/40 focus:ring-2 focus:ring-marca-soft outline-none"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !senha}
                className="w-full py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-marca to-marca-2-ink hover:opacity-95 disabled:opacity-50 transition"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
              {erro && <p className="text-red-600 text-sm text-center">{erro}</p>}
            </form>

            <div className="mt-4 flex flex-col gap-2 text-sm text-center">
              <Link href="/esqueci-senha" className="text-marca hover:text-marca-ink underline font-semibold">
                Esqueci minha senha
              </Link>
              <button onClick={trocarEmail} className="text-tinta-3 hover:text-marca underline">
                Não é meu email · trocar
              </button>
            </div>
          </>
        )}

        {/* Etapa: email não encontrado */}
        {etapa === 'nao_encontrado' && (
          <>
            <p className="text-center text-tinta-3 text-sm mb-6">
              Não encontramos uma compra com esse email.
            </p>
            <div className="bg-atencao-soft border border-atencao/25 rounded-lg p-4 text-sm text-atencao">
              <p className="font-medium mb-2">{email}</p>
              <p>Confere se digitou direito. Se tá certo e mesmo assim não funciona, pede acesso pro suporte.</p>
            </div>
            <Link
              href={`/solicitar-acesso?email=${encodeURIComponent(email)}`}
              className="mt-4 block w-full py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-marca to-marca-2-ink hover:opacity-95 text-center transition"
            >
              Falar com o suporte
            </Link>
            <button
              onClick={trocarEmail}
              className="mt-3 w-full text-sm text-tinta-3 hover:text-marca underline"
            >
              Tentar com outro email
            </button>
          </>
        )}

        {/* Etapa: compra cancelada/reembolsada */}
        {etapa === 'cancelado' && (
          <>
            <p className="text-center text-tinta-3 text-sm mb-6">
              Tem uma situação com sua compra
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
              <p className="font-medium mb-2">{email}</p>
              <p>
                {motivoCancelado === 'reembolso' && 'Sua compra foi reembolsada — por isso o acesso tá fechado.'}
                {motivoCancelado === 'chargeback' && 'Identificamos um chargeback na sua compra — o acesso ficou suspenso.'}
                {(motivoCancelado === 'expirada' || !motivoCancelado) && 'Sua assinatura expirou ou foi cancelada.'}
                {' '}Se você acha que isso é um engano, fala com o suporte e a gente resolve.
              </p>
            </div>
            <Link
              href={`/solicitar-acesso?email=${encodeURIComponent(email)}&motivo=acesso_${motivoCancelado || 'cancelado'}`}
              className="mt-4 block w-full py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-marca to-marca-2-ink hover:opacity-95 text-center transition"
            >
              Falar com o suporte
            </Link>
            <button
              onClick={trocarEmail}
              className="mt-3 w-full text-sm text-tinta-3 hover:text-marca underline"
            >
              Tentar com outro email
            </button>
          </>
        )}

        {/* Etapa: bloqueado */}
        {etapa === 'bloqueado' && (
          <>
            <p className="text-center text-tinta-3 text-sm mb-6">
              Muitas tentativas erradas
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900 text-center">
              <p className="font-medium mb-2">Acesso bloqueado temporariamente</p>
              <p className="text-2xl font-bold tabular-nums my-2">
                {Math.floor(contagem / 60)}:{String(contagem % 60).padStart(2, '0')}
              </p>
              <p>Tenta de novo quando o tempo zerar.</p>
            </div>
            <Link
              href={`/solicitar-acesso?email=${encodeURIComponent(email)}`}
              className="mt-4 block w-full py-3 rounded-lg font-semibold text-marca border border-marca/20 hover:bg-marca-soft text-center transition"
            >
              Falar com o suporte
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
